<template>
    <application-layout
        :property="property"
        :application="application"
        :experian_attempt="experian_attempt"
        :step_completion_status="$page.props.step_completion_status"
        :step_progress_data="$page.props.step_progress_data"
        :completed_steps_map="$page.props.completed_steps_map"
        current-step="terms_and_conditions"
        sub-title=" - Terms and Conditions"
    >
        <div class="relative pb-6">
            <div class="bg-gray-50 rounded-lg shadow-md p-6">
                <form class="space-y-8 divide-y divide-gray-200" @submit.prevent="update">
                    <terms-and-conditions-form
                        :terms="terms"
                    />
                    <renter-report-share-agreement
                        v-model="form.renter_report_share"
                        :error="form.errors.renter_report_share"
                        :form="form"
                    />

                    <california-property-agreement
                        v-if="form.is_ca_property"    
                        v-model="form.ca_consent"
                        :error="form.errors.ca_consent"
                        :form="form"
                    />

                    <user-acknowledgment-statement
                        v-model="form.user_acknowledgment"
                        :error="form.errors.user_acknowledgment"
                        :form="form"
                    />

                    <!-- Digital Signature Section -->
                    <div class="mt-6 bg-white rounded-md shadow-sm border border-gray-200">
                        <div class="py-4 px-6 border-b border-gray-200">
                            <h3 class="text-xl font-bold text-gray-700">Signature</h3>
                            <p class="text-sm text-gray-500 mt-1">Please sign below to confirm your acceptance of these terms</p>
                        </div>
                        <div class="p-6">
                            <signature-pad
                                ref="tosSignature"
                                label="Digital Signature"
                                :required="true"
                                :error="form.errors.tos_signature"
                            />
                        </div>
                    </div>

                    <!-- Footer with Submit Button -->
                    <div class="mt-6 bg-white rounded-md shadow-md border border-gray-200">
                        <div class="border-t border-gray-200 px-6 py-4">
                            <div class="flex items-center justify-end">
                                <form-button
                                    :form="form"
                                    type="submit"
                                >
                                    I Agree & Continue
                                </form-button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <!-- SSN acknowledgment modal: shows on mount if applicant has not yet acknowledged. -->
        <ssn-acknowledgment-modal
            :show="showSsnAcknowledgmentModal"
            @closeAlert="handleCloseModal"
        />

        <!-- No-refund modal: chained after SSN ack, or shown directly on mount if SSN is already acknowledged but refund is not. -->
        <refund-policy-modal
            v-if="showRefundPolicyModal"
            @closeAlert="handleRefundCloseModal"
        />
    </application-layout>
</template>

<script>
import axios from 'axios';
import ApplicationLayout from './ApplicationLayout.vue';
import RenterReportShareAgreement from './RenterReportShareAgreement.vue';
import TermsAndConditionsForm from '@/Components/TermsAndConditions/TermsAndConditionsForm.vue';
import FormButton from '@/Shared/FormButton.vue';
import { useForm } from '@inertiajs/vue3';
import UserAcknowledgmentStatement from './UserAcknowledgmentStatement.vue';
import CaliforniaPropertyAgreement from './CaliforniaPropertyAgreement.vue';
import SignaturePad from '@/Components/SignaturePad.vue';
import SsnAcknowledgmentModal from '@/Shared/SsnAcknowledgmentModal.vue';
import RefundPolicyModal from '@/Shared/RefundPolicyModal.vue';

export default {
    components: {
        ApplicationLayout,
        TermsAndConditionsForm,
        RenterReportShareAgreement,
        FormButton,
        UserAcknowledgmentStatement,
        CaliforniaPropertyAgreement,
        SignaturePad,
        SsnAcknowledgmentModal,
        RefundPolicyModal,
    },
    props: {
        property: Object,
        application: Object,
        step_completion_status: Object,
        step_progress_data: Object,
        completed_steps_map: Object,
        terms: { type: String, default: null },
    },
     data() {
        return {
            showSsnAcknowledgmentModal: false,
            showRefundPolicyModal: false,
            form: useForm({
                terms_and_conditions: 1,
                newsletter: true,
                renter_report_share : 0,
                is_ca_property: false,
                ca_consent: 0,
                tos_signature: null,
            }),
        }
    },
    mounted() {
        // Update the form with actual application data once component is mounted
        this.form.is_ca_property = this.application?.is_ca_property || false;

        // Trigger the SSN -> Refund acknowledgment chain on mount, capturing both
        // for chargeback-defense. SSN modal closes -> handleCloseModal opens refund.
        // If SSN is already acknowledged but refund is not, jump straight to refund.
        const ssnAcked = !!this.application?.applicant_ssn_acknowledgment;
        const refundAcked = !!this.application?.no_refund_acknowledgment;

        if (!ssnAcked) {
            this.showSsnAcknowledgmentModal = true;
        } else if (!refundAcked) {
            this.showRefundPolicyModal = true;
        }
    },
    methods: {
        update() {
            if (this.$refs.tosSignature.isEmpty()) {
                // Use the SignaturePad's :error prop instead of a browser alert so the
                // message renders inline next to the empty pad, matching the rest of
                // the page's validation UX.
                this.form.errors.tos_signature = 'Please sign your name in the Signature box before continuing.';
                this.$refs.tosSignature.$el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return false;
            }
            this.form.errors.tos_signature = null;
            this.form.tos_signature = this.$refs.tosSignature.save();
            this.form.post(this.route('application.update-terms-and-conditions', this.application.id))
        },
        handleCloseModal() {
          axios.post(route('ssn-acknowledgment'), {
            type: 'application',
            value: this.application.id,
          }).then(response => {
            if(response.data.status) {
              this.showSsnAcknowledgmentModal = false;
              this.showRefundPolicyModal = true;
            }
          });
        },
        handleRefundCloseModal() {
          axios.post(route('refund-policy-acknowledgment'), {
            type: 'application',
            value: this.application.id,
          }).then(response => {
            if(response.data.status) {
              this.showRefundPolicyModal = false;
            }
          });
        }
    }
}
</script>